import { useState, useEffect, useCallback } from 'react';
import {
    getTeamAssets,
    createTeamAsset,
    updateTeamAsset,
    deleteTeamAsset
} from 'team/apis/teamAssetApi';

/**
 * Custom hook to manage team assets
 * @param {string} teamId - Team ID
 * @param {string|null} typeFilter - Optional asset type filter
 */
export const useTeamAssets = (teamId, typeFilter = null) => {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchAssets = useCallback(async () => {
        if (!teamId) {
            setAssets([]);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await getTeamAssets(teamId, typeFilter);
            if (response?.success) {
                setAssets(response.assets || []);
            } else {
                throw new Error(response?.message || 'Failed to fetch team assets');
            }
        } catch (err) {
            console.error('Error fetching team assets:', err);
            setError(err.message);
            setAssets([]);
        } finally {
            setLoading(false);
        }
    }, [teamId, typeFilter]);

    useEffect(() => {
        fetchAssets();
    }, [fetchAssets]);

    const createAsset = useCallback(async (assetData) => {
        if (!teamId) {
            throw new Error('Team ID is required');
        }

        try {
            setLoading(true);
            setError(null);
            const response = await createTeamAsset(teamId, assetData);
            if (response?.success) {
                await fetchAssets();
                return response;
            }
            throw new Error(response?.message || 'Failed to create team asset');
        } catch (err) {
            console.error('Error creating team asset:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [teamId, fetchAssets]);

    const updateAsset = useCallback(async (assetId, updateData) => {
        if (!teamId) {
            throw new Error('Team ID is required');
        }

        try {
            setLoading(true);
            setError(null);
            const response = await updateTeamAsset(teamId, assetId, updateData);
            if (response?.success) {
                await fetchAssets();
                return response;
            }
            throw new Error(response?.message || 'Failed to update team asset');
        } catch (err) {
            console.error('Error updating team asset:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [teamId, fetchAssets]);

    const deleteAsset = useCallback(async (assetId) => {
        if (!teamId) {
            throw new Error('Team ID is required');
        }

        try {
            setLoading(true);
            setError(null);
            const response = await deleteTeamAsset(teamId, assetId);
            if (response?.success) {
                await fetchAssets();
                return response;
            }
            throw new Error(response?.message || 'Failed to delete team asset');
        } catch (err) {
            console.error('Error deleting team asset:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [teamId, fetchAssets]);

    return {
        assets,
        loading,
        error,
        refreshAssets: fetchAssets,
        createAsset,
        updateAsset,
        deleteAsset
    };
};
